import { UserStatus } from '../../../services/certreq/userView/userStatus';

/**
 * Fargespråket i søkervisningen: hvilken av temaets alarmfarger hver status
 * låner flate og kant fra.
 *
 * Valgene står samlet her, og ikke spredt ut i de enkelte stilblokkene, slik at
 * «hvilken familie betyr hva» er ett valg på ett sted. Kravradene,
 * alternativboksene og opplastingsknappen leser den samme paletten.
 *
 * **Oppfylt og Dokumentert deler grønt.** Begge er gode nyheter: kravet er
 * enten avklart av motoren, eller så ligger papiret inne og venter på kontroll.
 * Skillet mellom dem bæres av ansiktet (😀 mot 😍) og av statusordet, som
 * fortsatt ligger på raden for skjermlesere.
 *
 * **Mangler er gult, ikke rødt.** Rødt betyr feil eller fare. Et krav som ikke
 * er oppfylt er hverken: søkeren har ikke gjort noe galt, hun har bare noe
 * igjen å gjøre. Den gule familien ber om handling uten å skremme.
 *
 * **Ikke aktuelt er nøytralt** — en vei brukeren ikke følger skal være stille.
 *
 * Fargene bærer aldri betydning alene: ansiktet viser statusen for øyet, og
 * statusordet ligger som visuelt skjult tekst på raden for skjermlesere.
 */
export type StatusPalette = {
  /** Radens flate. */
  surface: string;
  /** Radens kant, og venstrekanten på en alternativboks. */
  border: string;
  /** Den mørkeste kanten i familien — kant på opplastingsknappen. */
  strong: string;
  /** Tekstfarge på flaten. */
  text: string;
};

// `theme` er `any` med vilje: `DefaultTheme` er tom i denne klienten (ingen
// modulutvidelse), og styled-components lar den da falle tilbake til `any` inne
// i stilblokkene. Å skrive den som `DefaultTheme` her ville brutt kompileringen.
export const statusPalette = (theme: any, status: UserStatus): StatusPalette => {
  const { alert, background, border, font } = theme.colors;

  switch (status) {
    case 'fulfilled':
    case 'uploaded':
      return {
        surface: alert.successLight,
        border: alert.successDark,
        strong: alert.successDarker,
        text: font.text,
      };
    case 'missing':
      return {
        surface: alert.warningLight,
        border: alert.warningDark,
        strong: alert.warningDarker,
        text: font.text,
      };
    default:
      return {
        surface: background.secondary,
        border,
        strong: font.text75,
        text: font.text75,
      };
  }
};

/**
 * Statusvariablene som CSS, klare til å legges på en node. Radene, boksene og
 * knappen leser dem videre med `var(--status-*)`, slik at knappen aldri kan
 * komme i utakt med raden den står under.
 */
export const statusVariables = (theme: any, status: UserStatus): string => {
  const palette = statusPalette(theme, status);

  return [
    `--status-surface: ${palette.surface};`,
    `--status-border: ${palette.border};`,
    `--status-strong: ${palette.strong};`,
    `--status-text: ${palette.text};`,
  ].join('\n');
};

/** Alle statusene, brukt til å skrive ut én blokk per `data-status`. */
export const USER_STATUSES: UserStatus[] = ['fulfilled', 'uploaded', 'missing', 'notApplicable'];
